import React from 'react';
import styled from 'styled-components';
import { Plus, Folder, Tag, Lock, TrendingUp, Clock } from 'lucide-react';
import { theme } from '../../styles/theme';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { useApiOnMount } from '../../hooks/useApi';
import { spaceService } from '../../services/spaceService';
import { secretService } from '../../services/secretService';
import { labelService } from '../../services/labelService';
import { formatRelativeTime, getSecretTypeIcon } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const WelcomeSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const WelcomeText = styled.div`
  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ $color, theme }) => theme.colors[$color] || theme.colors.primary}20;
  color: ${({ $color, theme }) => theme.colors[$color] || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SectionAction = styled(Button)`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
`;

const SpaceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SpaceItem = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const SpaceColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || theme.colors.primary};
  flex-shrink: 0;
`;

const SpaceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SpaceName = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpaceDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpaceStats = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: right;
`;

const RecentSecrets = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SecretItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surface};
  }
`;

const SecretIcon = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  flex-shrink: 0;
`;

const SecretInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SecretTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SecretTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  }
`;

function DashboardPage() {
  const navigate = useNavigate();
  
  const { data: spaces, loading: spacesLoading } = useApiOnMount(spaceService.getSpaces);
  const { data: recentSecrets, loading: secretsLoading } = useApiOnMount(() => 
    secretService.searchSecrets({ limit: 5 })
  );
  const { data: labels, loading: labelsLoading } = useApiOnMount(labelService.getLabels);

  const handleCreateSpace = () => {
    navigate('/spaces');
  };

  const handleSpaceClick = (spaceId) => {
    navigate(`/spaces/${spaceId}`);
  };

  const handleSecretClick = (secretId) => {
    navigate(`/secrets/${secretId}`);
  };

  const totalSecrets = spaces?.reduce((total, space) => total + (space.secrets?.length || 0), 0) || 0;

  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeText>
          <h1>Welcome back!</h1>
          <p>Here's what's happening with your secrets</p>
        </WelcomeText>
        <QuickActions>
          <Button variant="primary" onClick={handleCreateSpace}>
            <Plus size={16} />
            New Space
          </Button>
        </QuickActions>
      </WelcomeSection>

      <StatsGrid>
        <StatCard>
          <StatIcon $color="primary">
            <Folder size={24} />
          </StatIcon>
          <StatContent>
            <StatValue>{spaces?.length || 0}</StatValue>
            <StatLabel>Spaces</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="success">
            <Lock size={24} />
          </StatIcon>
          <StatContent>
            <StatValue>{totalSecrets}</StatValue>
            <StatLabel>Secrets</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="info">
            <Tag size={24} />
          </StatIcon>
          <StatContent>
            <StatValue>{labels?.length || 0}</StatValue>
            <StatLabel>Labels</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="warning">
            <TrendingUp size={24} />
          </StatIcon>
          <StatContent>
            <StatValue>0</StatValue>
            <StatLabel>This Week</StatLabel>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <ContentGrid>
        <Section>
          <SectionHeader>
            <SectionTitle>
              <Folder size={20} />
              Recent Spaces
            </SectionTitle>
            <SectionAction variant="ghost" onClick={() => navigate('/spaces')}>
              View All
            </SectionAction>
          </SectionHeader>

          {spacesLoading ? (
            <div>Loading spaces...</div>
          ) : spaces?.length > 0 ? (
            <SpaceList>
              {spaces.slice(0, 5).map((space) => (
                <SpaceItem
                  key={space.id}
                  onClick={() => handleSpaceClick(space.id)}
                >
                  <SpaceColor $color={space.color} />
                  <SpaceInfo>
                    <SpaceName>{space.name}</SpaceName>
                    <SpaceDescription>{space.description || 'No description'}</SpaceDescription>
                  </SpaceInfo>
                  <SpaceStats>
                    {space.secrets?.length || 0} secrets
                  </SpaceStats>
                </SpaceItem>
              ))}
            </SpaceList>
          ) : (
            <EmptyState>
              <h3>No spaces yet</h3>
              <p>Create your first space to start organizing your secrets</p>
              <Button variant="primary" onClick={handleCreateSpace}>
                <Plus size={16} />
                Create Space
              </Button>
            </EmptyState>
          )}
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>
              <Clock size={20} />
              Recent Secrets
            </SectionTitle>
            <SectionAction variant="ghost" onClick={() => navigate('/spaces')}>
              View All
            </SectionAction>
          </SectionHeader>

          {secretsLoading ? (
            <div>Loading secrets...</div>
          ) : recentSecrets?.length > 0 ? (
            <RecentSecrets>
              {recentSecrets.map((secret) => (
                <SecretItem
                  key={secret.id}
                  onClick={() => handleSecretClick(secret.id)}
                >
                  <SecretIcon>
                    {getSecretTypeIcon(secret.type)}
                  </SecretIcon>
                  <SecretInfo>
                    <SecretTitle>{secret.title}</SecretTitle>
                    <SecretTime>{formatRelativeTime(secret.updated_at)}</SecretTime>
                  </SecretInfo>
                </SecretItem>
              ))}
            </RecentSecrets>
          ) : (
            <EmptyState>
              <h3>No recent secrets</h3>
              <p>Your recent secrets will appear here</p>
            </EmptyState>
          )}
        </Section>
      </ContentGrid>
    </DashboardContainer>
  );
}

export default DashboardPage;
